import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { menuItemStyle, textfieldText } from "../../style/AppointmentStyle";
import {
  orangeButton,
  primaryBtnTeal,
  selectStyles,
  textfieldTextPrep,
} from "../../style/PatientDetailsStyle";
import SearchIcon from "@mui/icons-material/Search";
import useDebounce from "../../hooks/UseDebounce";
import UseSearchPatient from "../../helper/UseSearchPatient";
import AddIcon from "@mui/icons-material/Add";
import CreatableSelect from "react-select/creatable";
import { Search } from "@mui/icons-material";
import { components } from "react-select";
import UseFetchMedicalTestList from "../../helper/UseFetchMedicalTestList";
import {
  externalLabReport,
  externalLabReportsFile,
  ExtractMedicineSummaryV1,
} from "../../connections/postApi";
import { uploadFileToS3 } from "../../connections/s3Upload";
import { postMedicalReport } from "../../connections/postApi"; // ✅ make sure this is exported
import { checkAvailableTest } from "../../connections/getApi";
import { showToast } from "../../utils/toastUtil";
import Select from "react-select";
import LabTestListAll from "../../helper/LabTestListAll";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import dayjs from "dayjs";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { drTableGreen } from "../../style/PatinetFormStyle";
import { headingStyle, xBtn } from "../../style/LapPageStyle";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ScreenShareOutlinedIcon from "@mui/icons-material/ScreenShareOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import Tooltip from "@mui/material/Tooltip";

const AddExternalLabReports = ({ onClose, refetchList }) => {
  const [inputValue, setInputValue] = useState("");
  const [patient, setPatient] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowLoading, setRowLoading] = useState({});
  const [generatedAt, setGeneratedAt] = useState(dayjs().format("YYYY-MM-DD"));
  const [openSearchDropDown, setOpenSearchDropDown] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [scanLoading, setScanLoading] = useState(false);
  const [extractedFileSet, setExtractedFileSet] = useState(new Set());

  // ✅ ref for tracking original values (for update comparison)
  const originalValueRef = useRef({});

  const makeKey = (type, id) => `${type}_${id}`;

  // ✅ Create modal state (Normal vs Composite)
  const [createModal, setCreateModal] = useState({
    open: false,
    rowIndex: null,
    label: "",
  });
  const [createChoice, setCreateChoice] = useState("normal");

  const openCreateTypeModal = (rowIndex, label) => {
    setCreateChoice("normal");
    setCreateModal({ open: true, rowIndex, label });
  };

  const closeCreateTypeModal = () => {
    setCreateModal({ open: false, rowIndex: null, label: "" });
  };

  const confirmCreateType = () => {
    const { rowIndex, label } = createModal;
    if (rowIndex === null) return;

    const updated = [...rows];
    updated[rowIndex].test = { label, value: label, __isNew__: true };

    const makeComposite = createChoice === "composite";
    updated[rowIndex].isComposite = makeComposite;

    if (makeComposite) updated[rowIndex].value = "";
    if (!makeComposite) updated[rowIndex].compositeRows = [];

    setRows(updated);
    closeCreateTypeModal();
  };

  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        test: null,
        value: "",
        isComposite: false,
        compositeRows: [],
        reportId: null, // ✅ track created report id
      },
    ]);
  };

  const handleRemoveRow = (index) => {
    const list = [...rows];
    list.splice(index, 1);
    setRows(list);
  };

  const debouncedInputValue = useDebounce(inputValue, 600);
  const { data: searchResult, isLoading } = UseSearchPatient({
    q: debouncedInputValue,
    page: 1,
    perPage: 10,
  });

  const [testSearch, setTestSearch] = useState("");
  const debouncedTestSearch = useDebounce(testSearch, 600);

  const isTyping = testSearch !== debouncedTestSearch;

  const {
    data: medicalTestLists,
    isLoading: medicalTestLoading,
    refetch: refetchMedicalTests,
  } = UseFetchMedicalTestList(1, 25, debouncedTestSearch);

  const testOptions =
    medicalTestLists?.payload?.medical_tests?.map((test) => ({
      value: test,
      label: test.name,
    })) || [];

  const exactMatchExists = testOptions.some(
    (opt) =>
      opt.label.toLowerCase().trim() ===
      debouncedTestSearch.toLowerCase().trim(),
  );

  const { data: labData } = LabTestListAll();

  const formattedLabOptions = (labData?.payload?.labs || []).map((l) => ({
    value: l.id,
    label: l.name,
  }));

  const isNewTestIncomplete = (row) => {
    return !row.lab_id || !row.min || !row.max || !row.critical;
  };

  const shouldFetchTests = debouncedTestSearch?.trim().length > 0;

  const canCreate =
    debouncedTestSearch.length > 0 &&
    !isTyping &&
    !medicalTestLoading &&
    !exactMatchExists;

  const isCompositeFromApi = (selected) => {
    const v = selected?.value;
    return Boolean(v?.composite === true);
  };

  const isLockedComposite = (row) => {
    const v = row?.test?.value;
    return Boolean(
      row?.isComposite &&
      !row?.test?.__isNew__ &&
      v?.composite_fields?.length > 0,
    );
  };

  const subTestOptions = useMemo(() => {
    return testOptions.filter((opt) => !(opt?.value?.composite === true));
  }, [testOptions]);

  // =============================================================
  // ✅ UPDATE: Normal test (called when reportId already exists)
  // =============================================================
  const handleEditMedicalReportValue = async (
    item,
    newValue,
    force = false,
  ) => {
    if (newValue === undefined || newValue === null) return;

    const key = makeKey("normal", item?.id);
    const oldValue = originalValueRef.current[key];
    const prevValue = oldValue !== undefined ? oldValue : (item?.value ?? "");

    if (!force && String(prevValue) === String(newValue)) {
      delete originalValueRef.current[key];
      return;
    }

    const payload = {
      medical_test_report: {
        patient_id: item?.patient_id,
        medical_test_id: item?.medical_test_id,
        invoice_item_id: item?.invoice_item_id,
        value: newValue,
      },
      id: item?.id,
    };

    try {
      const result = await postMedicalReport(
        payload,
        item?.report_id ?? item?.id,
      );
      showToast(
        result?.status?.message || result?.status?.status,
        result?.status?.status,
      );
    } catch (e) {
      showToast("Something went wrong", "ERROR");
    } finally {
      delete originalValueRef.current[key];
    }
  };

  // =============================================================
  // ✅ UPDATE: Composite sub-test (called when reportId already exists)
  // =============================================================
  const handleEditMedicalReportValueComp = async (
    item,
    newValue,
    force = false,
  ) => {
    if (newValue === undefined || newValue === null) return;

    const key = makeKey("comp", item?.id);
    const oldValue = originalValueRef.current[key];
    const prevValue = oldValue !== undefined ? oldValue : (item?.value ?? "");

    if (!force && String(prevValue) === String(newValue)) {
      delete originalValueRef.current[key];
      return;
    }

    const payload = {
      medical_test_report: {
        patient_id: item?.patient_id,
        medical_test_id: item?.medical_test_id,
        invoice_item_id: item?.invoice_item_id,
        value: newValue,
        composite_parent_test_id: item?.composite_parent_test_id,
      },
      ...(item?.medical_test_report_id && { id: item?.medical_test_report_id }),
    };

    try {
      const result = await postMedicalReport(
        payload,
        item?.report_id ?? item?.medical_test_report_id ?? item?.id,
      );
      showToast(
        result?.status?.message || result?.status?.status,
        result?.status?.status,
      );
    } catch (e) {
      showToast("Something went wrong", "ERROR");
    } finally {
      delete originalValueRef.current[key];
    }
  };

  // =============================================================
  // ✅ NORMAL TEST BLUR
  //    - If reportId exists on row → call UPDATE (postMedicalReport)
  //    - Otherwise → call CREATE (externalLabReport) and store reportId
  // =============================================================
  const handleValueBlur = async (row, index) => {
    if (!patient) {
      showToast("Please select a patient first", "ERROR");
      return;
    }

    if (!row.test || !row.value) return;
    if (row.isComposite) return;
    if (row.test?.__isNew__) return; // new tests use handleCreateNormalTest button
    if (row.aiExtracted) return; // AI rows use Confirm button

    console.log(row, "row");
    // ✅ Already created → call UPDATE API
    if (row?.reportId) {
      const item = {
        id: row.reportId,
        report_id: row.reportId,
        patient_id: patient?.id,
        medical_test_id: row.test?.value?.id,
        invoice_item_id: row.test?.value?.invoice_item_id ?? null,
        value: row.value,
      };
      // store original for comparison
      // const key = makeKey("normal", row.reportId);
      // if (originalValueRef.current[key] === undefined) {
      //   originalValueRef.current[key] = row.value;
      // }
      await handleEditMedicalReportValue(item, row.value);
      return;
    }

    // ✅ First time → CREATE
    const reportObject = {
      patient_id: patient?.id,
      name: row.test.value.name,
      code: row.test.value.code,
      value: row.value,
      generated_at: generatedAt,
      lab_id: row.test.value.lab_id,
      medical_test_category_id: row.test.value.medical_test_category_id,
    };

    const body = { medical_test_reports: [reportObject] };

    setRowLoading((prev) => ({ ...prev, [index]: true }));

    try {
      const res = await externalLabReport(body);

      showToast(
        res?.status?.message || res?.status?.status,
        res?.status?.status,
      );

      // ✅ Store reportId so future blurs call UPDATE
      const created = res?.payload?.[0];
      // console.log(created, "Created");
      if (created?.id) {
        const updated = [...rows];
        updated[index].reportId = created?.id;
        setRows(updated);
      }
    } catch (err) {
      console.error("API ERROR:", err);
    } finally {
      setRowLoading((prev) => ({ ...prev, [index]: false }));
    }
  };

  // =============================================================
  // ✅ CREATE NEW NORMAL TEST (button click)
  //    Stores reportId after success so blurs call UPDATE
  // =============================================================
  const handleCreateNormalTest = async (row, index) => {
    if (!patient) {
      showToast("Please select a patient first", "ERROR");
      return;
    }
    if (!row?.test?.__isNew__) return;
    if (row.isComposite) return;
    if (!row.value) {
      showToast("Please enter value", "ERROR");
      return;
    }
    if (isNewTestIncomplete(row)) {
      showToast("Please fill Lab, Min, Max & Critical Range", "ERROR");
      return;
    }

    const reportObject = {
      patient_id: patient?.id,
      name: row.test.label,
      code: row.test.label,
      value: row.value,
      generated_at: generatedAt,
      lab_id: row?.lab_id?.value,
      min_range: row.min || null,
      max_range: row.max || null,
      critical_range: row.critical || null,
    };

    const body = { medical_test_reports: [reportObject] };

    setRowLoading((prev) => ({ ...prev, [`create_${index}`]: true }));

    try {
      const res = await externalLabReport(body);
      const created = res?.payload?.[0];

      if (res?.status?.status === "SUCCESS" && created?.medical_test_id) {
        const updated = [...rows];

        // ✅ Convert to existing test object
        updated[index].test = {
          label: created.test_name || row.test.label,
          value: {
            id: created.medical_test_id,
            name: created.test_name || row.test.label,
            code: created.test_name || row.test.label,
            lab_id: created.lab_id || row?.lab_id?.value,
            medical_test_category_id: created.medical_test_category_id ?? null,
          },
        };

        // ✅ Store reportId so future blurs call UPDATE
        if (created?.id) {
          updated[index].reportId = created.id;
        }

        // ✅ Clear aiExtracted so the yellow confirm tick doesn't reappear
        updated[index].aiExtracted = false;

        setRows(updated);
      }

      showToast(
        res?.status?.message || res?.status?.status,
        res?.status?.status,
      );
    } catch (err) {
      console.error("API ERROR:", err);
    } finally {
      setRowLoading((prev) => ({ ...prev, [`create_${index}`]: false }));
    }
  };

  // =============================================================
  // ✅ COMPOSITE SUB-TEST BLUR
  //    - If subRow.reportId exists → call UPDATE (postMedicalReport)
  //    - Otherwise → call CREATE (externalLabReport) and store reportId
  // =============================================================
  const handleCompositeValueBlur = async (parentRowIndex, subRowIndex) => {
    const parent = rows[parentRowIndex];
    const sub = parent?.compositeRows?.[subRowIndex];

    if (!patient) return showToast("Please select a patient first", "ERROR");
    if (!parent?.test || !parent.isComposite) return;
    if (!sub?.test || !sub?.value) return;
    if (parent?.aiExtracted && !confirmingRef.current) return; // AI rows use Confirm button

    const parentId = getCompositeParentTestId(parent);
    if (!parentId) return; // new composite parent → skip blur, use Save button

    const isNewSub = sub?.test?.__isNew__;
    if (isNewSub && isNewSubTestIncomplete(sub)) return;

    // ✅ Already created → call UPDATE API
    if (sub.reportId) {
      const item = {
        id: sub.reportId,
        report_id: sub.reportId,
        medical_test_report_id: sub.reportId,
        patient_id: patient?.id,
        medical_test_id: isNewSub ? sub.test?.value?.id : sub.test?.value?.id,
        invoice_item_id: sub.test?.value?.invoice_item_id ?? null,
        composite_parent_test_id: parentId,
        value: sub.value,
      };

      await handleEditMedicalReportValueComp(item, sub.value);
      return;
    }

    // ✅ First time → CREATE
    const body = {
      medical_test_reports: [
        {
          patient_id: patient?.id,
          name: isNewSub ? sub.test.label : sub.test.value.name,
          code: isNewSub ? sub.test.label : sub.test.value.code,
          value: sub.value,
          generated_at: generatedAt,
          composite_parent_test_id: parentId,
          ...(isNewSub
            ? {
                lab_id: sub?.lab_id?.value,
                medical_test_category_id: null,
                min_range: sub.min || null,
                max_range: sub.max || null,
                critical_range: sub.critical || null,
              }
            : {
                lab_id: sub.test.value.lab_id,
                medical_test_category_id:
                  sub.test.value.medical_test_category_id,
              }),
        },
      ],
    };

    const key = `c_${parentRowIndex}_${subRowIndex}`;
    setRowLoading((prev) => ({ ...prev, [key]: true }));

    try {
      const res = await externalLabReport(body);

      showToast(res?.status?.message || "Saved", res?.status?.status);

      // ✅ Store reportId so future blurs call UPDATE
      const created = res?.payload?.[0];
      if (created?.id) {
        const updated = [...rows];
        updated[parentRowIndex].compositeRows[subRowIndex].reportId =
          created.id;
        setRows(updated);
      }
    } catch (err) {
      console.error("API ERROR:", err);
    } finally {
      setRowLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  // =============================================================
  // ✅ CREATE NEW COMPOSITE + ALL SUB-REPORTS (Save Composite button)
  //    Stores reportId on each subRow after success
  // =============================================================
  const handleCreateCompositeAndReports = async (parentIndex) => {
    const parent = rows[parentIndex];

    if (!patient) return showToast("Please select a patient first", "ERROR");
    if (!parent?.test || !parent.isComposite) return;

    if (parent?.test?.__isNew__ && !parent?.lab_id) {
      return showToast("Please choose lab for composite test", "ERROR");
    }

    const subs = parent.compositeRows || [];
    const validSubs = subs.filter((s) => s?.test && s?.value);

    if (validSubs.length === 0) {
      return showToast("Please add at least 1 sub-test value", "ERROR");
    }

    const hasInvalidNewSub = validSubs.some(
      (s) => s?.test?.__isNew__ && isNewSubTestIncomplete(s),
    );
    if (hasInvalidNewSub) {
      return showToast(
        "Please fill Lab, Min, Max & Critical for new sub-tests",
        "ERROR",
      );
    }

    const body = {
      ...(parent.test?.__isNew__ && {
        composite_medical_test: buildCompositeMedicalTestPayload(parent),
      }),
      medical_test_reports: validSubs.map((sub) => {
        const isNewSub = sub?.test?.__isNew__;
        return {
          patient_id: patient?.id,
          name: isNewSub ? sub.test.label : sub.test.value.name,
          code: isNewSub ? sub.test.label : sub.test.value.code,
          value: sub.value,
          generated_at: generatedAt,
          composite_parent_test_id: parent.test?.__isNew__
            ? ""
            : getCompositeParentTestId(parent),
          ...(isNewSub
            ? {
                lab_id: sub?.lab_id?.value,
                medical_test_category_id: null,
                min_range: sub.min || null,
                max_range: sub.max || null,
                critical_range: sub.critical || null,
              }
            : {
                lab_id: sub.test.value.lab_id,
                medical_test_category_id:
                  sub.test.value.medical_test_category_id,
              }),
        };
      }),
    };

    setRowLoading((prev) => ({
      ...prev,
      [`comp_create_${parentIndex}`]: true,
    }));

    try {
      const res = await externalLabReport(body);

      if (res?.status?.status !== "SUCCESS") {
        showToast(res?.status?.message || "Failed", "ERROR");
        return;
      }

      const updated = [...rows];

      // ✅ Store reportId on each sub-row from response payload
      const createdReports = res?.payload || [];
      createdReports.forEach((created) => {
        const matchIndex = updated[parentIndex].compositeRows.findIndex(
          (sub) =>
            sub.test?.value?.id === created.medical_test_id ||
            sub.test?.label === created.test_name,
        );
        if (matchIndex !== -1 && created?.id) {
          updated[parentIndex].compositeRows[matchIndex].reportId = created.id;
        }
      });

      // ✅ If parent was new composite → convert to existing so Save Composite button hides
      if (parent.test?.__isNew__) {
        const compositeParentId = createdReports[0]?.composite_parent_test_id;

        const refetchRes = await refetchMedicalTests?.();
        const latestTests =
          refetchRes?.data?.payload?.medical_tests ||
          medicalTestLists?.payload?.medical_tests ||
          [];

        const createdComposite = latestTests.find(
          (t) =>
            (t?.name || "").toLowerCase().trim() ===
              parent.test.label.toLowerCase().trim() && t?.composite === true,
        );

        if (createdComposite?.id) {
          updated[parentIndex].test = {
            label: createdComposite.name,
            value: createdComposite,
          };
          updated[parentIndex].composite_parent_test_id = createdComposite.id;
        } else {
          // Fallback: refetch didn't find the composite yet — still transition
          // to locked mode so "Save Composite" button hides and duplicates are prevented
          updated[parentIndex].test = {
            label: parent.test.label,
            value: {
              id: compositeParentId || null,
              name: parent.test.label,
              code: parent.test.label,
              composite: true,
              composite_fields: validSubs.map((s) => ({
                id: s.test?.value?.id || null,
                name: s.test?.label || s.test?.value?.name || "",
              })),
            },
          };
          if (compositeParentId) {
            updated[parentIndex].composite_parent_test_id = compositeParentId;
          }
        }
      }

      setRows(updated);
      showToast(res?.status?.message || "Composite saved", "SUCCESS");
    } catch (err) {
      console.error("API ERROR:", err);
      showToast("Failed to save composite", "ERROR");
    } finally {
      setRowLoading((prev) => ({
        ...prev,
        [`comp_create_${parentIndex}`]: false,
      }));
    }
  };

  const confirmingRef = useRef(false);

  // Per-row confirm for AI-extracted normal (existing) tests
  const handleConfirmSingleAiRow = async (row, index) => {
    if (!patient) return showToast("Please select a patient first", "ERROR");
    if (!row.value) return showToast("Please enter a value first", "ERROR");
    if (row.test?.__isNew__ || row.isComposite) return;

    const reportObject = {
      patient_id: patient?.id,
      name: row.test.value.name,
      code: row.test.value.code,
      value: row.value,
      generated_at: generatedAt,
      lab_id: row.test.value.lab_id,
      medical_test_category_id: row.test.value.medical_test_category_id,
    };

    setRowLoading((prev) => ({ ...prev, [`ai_confirm_${index}`]: true }));

    try {
      const res = await externalLabReport({
        medical_test_reports: [reportObject],
      });
      showToast(
        res?.status?.message || res?.status?.status,
        res?.status?.status,
      );

      const created = res?.payload?.[0];
      setRows((prev) => {
        const updated = [...prev];
        if (updated[index]) {
          updated[index].aiExtracted = false;
          if (created?.id) updated[index].reportId = created.id;
        }
        return updated;
      });
    } catch {
      showToast("Something went wrong", "ERROR");
    } finally {
      setRowLoading((prev) => ({ ...prev, [`ai_confirm_${index}`]: false }));
    }
  };

  // Per-row confirm for AI-extracted existing composite tests
  const handleConfirmAiCompositeRow = async (parentIndex) => {
    if (!patient) return showToast("Please select a patient first", "ERROR");

    confirmingRef.current = true;
    setRowLoading((prev) => ({
      ...prev,
      [`ai_comp_confirm_${parentIndex}`]: true,
    }));

    try {
      const subs = rows[parentIndex]?.compositeRows || [];
      for (let si = 0; si < subs.length; si++) {
        if (!subs[si]?.value) continue;
        await handleCompositeValueBlur(parentIndex, si);
      }
      setRows((prev) => {
        const updated = [...prev];
        if (updated[parentIndex]) updated[parentIndex].aiExtracted = false;
        return updated;
      });
    } finally {
      confirmingRef.current = false;
      setRowLoading((prev) => ({
        ...prev,
        [`ai_comp_confirm_${parentIndex}`]: false,
      }));
    }
  };

  const buildCompositeMedicalTestPayload = (parentRow) => {
    return {
      code: parentRow.test?.label,
      name: parentRow.test?.label,
      lab_id: parentRow?.lab_id?.value,
      female_min: parentRow.female_min ?? parentRow.min ?? null,
      male_min: parentRow.male_min ?? parentRow.min ?? null,
      female_max: parentRow.female_max ?? parentRow.max ?? null,
      male_max: parentRow.male_max ?? parentRow.max ?? null,
      composite_fields: [],
      composite: true,
    };
  };

  const getCompositeParentTestId = (parentRow) => {
    if (!parentRow?.test?.__isNew__ && parentRow?.test?.value?.id) {
      return parentRow.test.value.id;
    }
    if (parentRow?.composite_parent_test_id)
      return parentRow.composite_parent_test_id;
    return null;
  };

  const addCompositeSubRow = (parentIndex) => {
    const updated = [...rows];
    updated[parentIndex].compositeRows = [
      ...(updated[parentIndex].compositeRows || []),
      {
        test: null,
        value: "",
        lab_id: null,
        min: "",
        max: "",
        critical: "",
        reportId: null, // ✅ track sub-report id
      },
    ];
    setRows(updated);
  };

  const isNewSubTestIncomplete = (subRow) => {
    return !subRow?.lab_id || !subRow?.min || !subRow?.max || !subRow?.critical;
  };

  const removeCompositeSubRow = (parentIndex, subIndex) => {
    const updated = [...rows];
    const list = [...(updated[parentIndex].compositeRows || [])];
    list.splice(subIndex, 1);
    updated[parentIndex].compositeRows = list;
    setRows(updated);
  };

  const mapAiTestsToRows = (medicalTests = [], existingTests = []) => {
    return medicalTests.map((item) => {
      const matched = existingTests.find(
        (t) =>
          (item?.name &&
            t?.name?.toLowerCase().trim() ===
              item?.name?.toLowerCase().trim()) ||
          (item?.code &&
            t?.code?.toLowerCase().trim() === item?.code?.toLowerCase().trim()),
      );

      const isComposite = Boolean(item?.composite);

      // Build composite sub-rows depending on whether the composite test exists in system
      let compositeRows = [];
      if (isComposite) {
        if (matched && matched?.composite_fields?.length > 0) {
          // ✅ Composite found in system — use system sub-fields (have proper IDs),
          //    overlay AI values by name match
          compositeRows = matched.composite_fields.map((systemSub) => {
            const aiSub = (item?.composite_fields || []).find(
              (s) =>
                s?.name &&
                s.name.toLowerCase().trim() ===
                  systemSub?.name?.toLowerCase().trim(),
            );
            return {
              test: { label: systemSub.name, value: systemSub },
              value: aiSub?.value ?? "",
              lab_id: null,
              min: "",
              max: "",
              critical: aiSub?.critical ?? "",
              reportId: null,
            };
          });
        } else {
          // ✅ Composite NOT found in system — map from AI composite_fields,
          //    try to match each sub-test individually
          compositeRows = (item?.composite_fields || []).map((sub) => {
            const matchedSub = existingTests.find(
              (t) =>
                sub?.name &&
                t?.name?.toLowerCase().trim() ===
                  sub?.name?.toLowerCase().trim(),
            );
            return {
              test: matchedSub
                ? { label: matchedSub.name, value: matchedSub }
                : { label: sub?.name || "", value: sub, __isNew__: true },
              value: sub?.value ?? "",
              lab_id: null,
              min: sub?.min ?? "",
              max: sub?.max ?? "",
              critical: sub?.critical ?? "",
              reportId: null,
            };
          });
        }
      }

      return {
        test: matched
          ? { label: matched.name, value: matched }
          : {
              label: item?.name || item?.code || "",
              value: item?.name || item?.code || "",
              __isNew__: true,
            },
        value: item?.value ?? "",
        isComposite,
        compositeRows,
        ai_code: item?.code || "",
        ai_name: item?.name || "",
        min: item?.min ?? "",
        max: item?.max ?? "",
        critical: item?.critical ?? "",
        unit: item?.unit || "",
        lab_id: null,
        reportId: null,
        aiExtracted: true,
      };
    });
  };

  const handleExtractFile = async (file, idx) => {
    if (!patient) {
      showToast("Please select a patient first", "ERROR");
      return;
    }

    try {
      setScanLoading(true);

      const { key } = await uploadFileToS3(file, "GlobalAiContext", 0);
      const res = await ExtractMedicineSummaryV1(key, "medical_test");
      const medicalTests = res?.payload?.summaries?.medical_tests || [];

      if (!medicalTests.length) {
        showToast("No tests found in the file", "ERROR");
        return;
      }

      const testNames = medicalTests.map((t) => t?.name).filter(Boolean);
      let existingTests = [];
      if (testNames.length > 0) {
        const checkRes = await checkAvailableTest(testNames);
        existingTests = checkRes?.payload?.medical_tests || [];
      }

      const newRows = mapAiTestsToRows(medicalTests, existingTests);
      setRows((prev) => [...prev, ...newRows]);
      setExtractedFileSet((prev) => new Set([...prev, file]));
      showToast("Tests extracted successfully", "SUCCESS");
    } catch (err) {
      console.error(err);
      showToast("Failed to extract tests", "ERROR");
    } finally {
      setScanLoading(false);
    }
  };

  const fileInputRef = useRef(null);

  const handleDeleteFile = (idx) => {
    setSelectedFiles((prev) => {
      const fileToRemove = prev[idx];
      setExtractedFileSet((s) => {
        const next = new Set(s);
        next.delete(fileToRemove);
        return next;
      });
      return prev.filter((_, i) => i !== idx);
    });
    // reset input so same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];
    const validFiles = files.filter((file) => allowedTypes.includes(file.type));
    if (validFiles.length !== files.length) {
      showToast("Only PDF or Images are allowed", "error");
    }
    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  const handleUploadDocument = async () => {
    const nonExtractedFiles = selectedFiles.filter(
      (f) => !extractedFileSet.has(f),
    );
    if (nonExtractedFiles.length === 0)
      return showToast("Please upload a file first", "error");
    if (!patient) return showToast("Please select a patient", "error");

    setLoading(true);

    try {
      const filesToUpload = selectedFiles.filter(
        (file) => !extractedFileSet.has(file),
      );
      const uploads = filesToUpload.map((file) => {
        const formData = new FormData();
        formData.append("document[doc_type]", "Lab Reports");
        formData.append("document[documentable_type]", "Patient");
        formData.append("document[documentable_id]", patient?.id);
        formData.append("document[generated_at]", generatedAt);
        formData.append("document[attachment]", file);
        return externalLabReportsFile(formData);
      });

      const results = await Promise.all(uploads);
      const anyFail = results.some((r) => r?.status?.status !== "SUCCESS");
      if (anyFail) return showToast("Some uploads failed", "ERROR");

      showToast("All reports uploaded successfully", "SUCCESS");
      setSelectedFiles([]);
      onClose();
      refetchList();
    } catch (err) {
      showToast("Upload failed", "ERROR");
    } finally {
      setLoading(false);
      refetchList();
    }
  };

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);

  const openViewer = (file) => {
    if (!file?.url) return;
    setViewerFile(file);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setViewerFile(null);
  };

  return (
    <Box
      sx={{
        minHeight: "92vh",
        p: 1,
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" },
        overflow: "auto",
        position: "relative",
      }}
    >
      {scanLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(255,255,255,0.75)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
            borderRadius: 2,
          }}
        >
          <CircularProgress size={44} sx={{ color: "neutral.teal" }} />
          <Typography
            sx={{ fontFamily: "Lato", fontWeight: 600, color: "#101828" }}
          >
            Extracting tests...
          </Typography>
        </Box>
      )}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
        mt={-0.5}
      >
        <Box></Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <AddIcon
            sx={{
              width: "36px",
              height: "36px",
              color: "neutral.teal",
              marginBottom: 1,
            }}
          />
          <Typography
            variant="heading1"
            fontWeight={600}
            gutterBottom
            sx={{ ...headingStyle }}
          >
            Add external report
          </Typography>
        </Box>
        <IconButton
          // sx={{ marginBottom: "0.5rem" }}
          onClick={() => {
            onClose();
            refetchList();
          }}
        >
          <CloseIcon size="small" sx={{ color: "red" }} />
        </IconButton>
      </Box>

      <>
        <Box
          sx={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
            overflow: "auto",
          }}
        >
          {/* Patient + Date + Upload */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              mt: 1,
              alignItems: "center",
            }}
          >
            {/* Patient Search or Selected Patient Display */}
            <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 55%" } }}>
              {patient ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    height: "45px",
                    borderRadius: "8px",
                    border: "1.5px solid",
                    borderColor: "neutral.teal",
                    backgroundColor: "#EEF9F7",
                  }}
                >
                  <PersonOutlineOutlinedIcon
                    sx={{ color: "neutral.teal", fontSize: 22, flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, overflow: "hidden" }}>
                    <Typography
                      sx={{
                        fontFamily: "Lato",
                        fontWeight: 700,
                        fontSize: "14px",
                        color: "#101828",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {patient.full_name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Lato",
                        fontSize: "12px",
                        color: "#667085",
                      }}
                    >
                      {patient.uid}
                      {patient.mobile ? ` · ${patient.mobile}` : ""}
                    </Typography>
                  </Box>
                  <Chip
                    label="Change"
                    size="small"
                    onClick={() => setPatient(null)}
                    sx={{
                      fontFamily: "Lato",
                      fontSize: "11px",
                      cursor: "pointer",
                      bgcolor: "white",
                      border: "1px solid",
                      borderColor: "neutral.teal",
                      color: "neutral.teal",
                      flexShrink: 0,
                    }}
                  />
                </Box>
              ) : (
                <Autocomplete
                  popupIcon={false}
                  clearIcon={false}
                  clearOnBlur={false}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setInputValue("");
                      setPatient(newValue);
                    }
                  }}
                  open={
                    openSearchDropDown &&
                    Boolean(inputValue?.trim()) &&
                    searchResult?.payload?.patients?.length > 0
                  }
                  noOptionsText=""
                  onOpen={() => setOpenSearchDropDown(true)}
                  onClose={() => setOpenSearchDropDown(false)}
                  onInputChange={(e, value) => setInputValue(value)}
                  options={searchResult?.payload?.patients ?? []}
                  filterOptions={(options) => options}
                  getOptionLabel={(option) =>
                    option.uid && option.full_name
                      ? `${option.uid} - ${option.full_name}`
                      : ""
                  }
                  renderOption={(props, option) => (
                    <MenuItem
                      key={option.id}
                      {...props}
                      sx={{ ...menuItemStyle }}
                    >
                      {`${option.uid} - ${option.full_name}`}
                    </MenuItem>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      placeholder="Search Patient Id, Name, Mobile, Email"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoading ? (
                              <CircularProgress size={18} />
                            ) : (
                              <SearchIcon sx={{ opacity: 0.4, mr: -1 }} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        ...textfieldTextPrep,
                        "& .MuiOutlinedInput-root": {
                          fontFamily: "Lato",
                          borderRadius: "8px",
                          height: "45px",
                        },
                      }}
                    />
                  )}
                />
              )}
            </Box>

            {/* Date — always visible */}
            <Box
              sx={{
                flex: { xs: "1 1 100%", md: "1 1 10%" },
                minWidth: "150px",
              }}
            >
              <TextField
                fullWidth
                type="date"
                label="Select Date"
                value={generatedAt}
                onChange={(e) => setGeneratedAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "Lato",
                    borderRadius: "8px",
                    height: "45px",
                  },
                }}
              />
            </Box>

            {/* Upload button — only after patient selected */}
            {patient && (
              <Box
                sx={{
                  flex: { xs: "1 1 100%", md: "1 1 20%" },
                  display: "flex",
                  minWidth: "190px",
                  maxWidth: "210px",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  component="label"
                  fullWidth
                  sx={{
                    ...primaryBtnTeal,
                    gap: 1,
                    height: "45px",
                    minWidth: "150px",
                  }}
                >
                  <FileUploadOutlinedIcon />
                  Upload Report
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="application/pdf,image/*"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                  />
                </Button>
              </Box>
            )}

            {/* Attachment files — will be submitted as documents */}
            {selectedFiles.length > 0 && (
              <Box
                sx={{
                  backgroundColor: "#F9FAFB",
                  borderRadius: "12px",
                  p: 0.5,
                  paddingX: 2,
                  width: "100%",
                }}
              >
                <Typography sx={{ fontWeight: 600, mb: 1, mt: 1 }}>
                  Attachments:
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
                  {selectedFiles.map((file, idx) => {
                    const fileURL = URL.createObjectURL(file);
                    const isImage = file.type?.startsWith("image/");
                    const isPDF = file.type === "application/pdf";
                    const isExtracted = extractedFileSet.has(file);

                    return (
                      <Box
                        key={idx}
                        sx={{
                          width: 300,
                          height: 140,
                          borderRadius: "14px",
                          backgroundColor: isExtracted ? "#EEF9F4" : "#EEF6F9",
                          position: "relative",
                          border: isExtracted
                            ? "1.5px solid #27AE60"
                            : "1px solid #E1E1E1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 2,
                        }}
                      >
                        {isExtracted && (
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 8,
                              left: 10,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.4,
                            }}
                          >
                            <CheckCircleOutlineIcon
                              sx={{ fontSize: 13, color: "#27AE60" }}
                            />
                            <Typography
                              sx={{
                                fontSize: "11px",
                                fontFamily: "Lato",
                                color: "#27AE60",
                                fontWeight: 600,
                              }}
                            >
                              Extracted
                            </Typography>
                          </Box>
                        )}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            display: "flex",
                            gap: 0.5,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              openViewer({
                                url: fileURL,
                                filename: file?.name,
                                content_type: file?.type,
                              })
                            }
                          >
                            <VisibilityOutlinedIcon
                              sx={{
                                fontSize: 18,
                                color: isExtracted ? "#27AE60" : "neutral.teal",
                              }}
                            />
                          </IconButton>
                          {!isExtracted && (
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteFile(idx)}
                            >
                              <DeleteForeverOutlinedIcon
                                sx={{ fontSize: 18, color: "red" }}
                              />
                            </IconButton>
                          )}
                          {!isExtracted && (
                            <Tooltip title="Extract tests using AI">
                              <IconButton
                                size="small"
                                onClick={() => handleExtractFile(file, idx)}
                                disabled={scanLoading}
                              >
                                <AutoAwesomeOutlinedIcon
                                  sx={{ fontSize: 18, color: "neutral.teal" }}
                                />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        <Box
                          sx={{
                            textAlign: "center",
                            flexDirection: "column",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {isPDF ? (
                            <PictureAsPdfOutlinedIcon
                              sx={{
                                fontSize: 42,
                                color: isExtracted ? "#27AE60" : "neutral.teal",
                              }}
                            />
                          ) : isImage ? (
                            <ImageOutlinedIcon
                              sx={{
                                fontSize: 42,
                                color: isExtracted ? "#27AE60" : "neutral.teal",
                              }}
                            />
                          ) : (
                            <DescriptionOutlinedIcon
                              sx={{
                                fontSize: 42,
                                color: isExtracted ? "#27AE60" : "neutral.teal",
                              }}
                            />
                          )}
                          <Typography
                            variant="subTitle116px"
                            sx={{
                              mt: 1,
                              maxWidth: "220px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontFamily: "Lato",
                              color: isExtracted ? "#27AE60" : "neutral.black",
                            }}
                          >
                            {file.name}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {patient &&
                  selectedFiles.filter((f) => !extractedFileSet.has(f)).length >
                    0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mb: 2,
                      }}
                    >
                      <Button
                        fullWidth
                        sx={{
                          ...primaryBtnTeal,
                          height: "45px",
                          minWidth: "190px",
                          width: "170px",
                          mt: 1,
                        }}
                        disabled={loading}
                        onClick={handleUploadDocument}
                      >
                        {loading ? (
                          <Typography sx={{ color: "white" }}>
                            Uploading..
                          </Typography>
                        ) : (
                          <Typography sx={{ color: "white" }}>
                            Submit Documents
                          </Typography>
                        )}
                      </Button>
                    </Box>
                  )}
              </Box>
            )}
          </Box>

          {/* Main Table */}
          {patient && (
            <Box
              mt={3}
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: "15px",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  ...drTableGreen,
                  backgroundColor: "neutral.backGround",
                  py: 1,
                  px: 1,
                }}
              >
                <Grid container spacing={1} alignItems="center">
                  <Grid item md={0.2}>
                    <Typography variant="body1_text_bold">S.No</Typography>
                  </Grid>
                  <Grid item md={6} sx={{ textAlign: "center" }}>
                    <Typography
                      variant="body1_text_bold"
                      sx={{ textAlign: "center" }}
                    >
                      Test Names
                    </Typography>
                  </Grid>
                  <Grid item md={5.3} sx={{ textAlign: "center" }}>
                    <Typography
                      variant="body1_text_bold"
                      sx={{ textAlign: "center" }}
                    >
                      Value
                    </Typography>
                  </Grid>
                  {rows?.length > 0 && (
                    <Grid item md={0.5} sx={{ textAlign: "left" }}>
                      <IconButton
                        id="addMoreBtn"
                        variant="contained"
                        sx={{
                          ...orangeButton,
                          display: "flex",
                          marginLeft: "auto",
                          marginRight: "10px",
                        }}
                        onClick={handleAddRow}
                      >
                        <AddIcon
                          sx={{
                            width: "22px",
                            color: "neutral.white",
                            height: "22px",
                            padding: "2px",
                          }}
                        />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Box px={2} py={1}>
                {rows?.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: "center",
                      border: "1px dashed #D0D5DD",
                      borderRadius: "12px",
                      background: "#F9FBFF",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#667085",
                        fontSize: "15px",
                        fontWeight: 500,
                      }}
                    >
                      No tests added yet
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddRow}
                      disabled={!patient}
                      sx={{
                        ...primaryBtnTeal,
                        height: "40px",
                        borderRadius: "10px",
                        px: 3,
                      }}
                    >
                      Add Manual Test
                    </Button>
                  </Box>
                ) : (
                  rows?.map((row, index) => {
                    const isNew = row.test?.__isNew__;
                    const locked = isLockedComposite(row);
                    return (
                      <Box
                        key={index}
                        sx={
                          row.aiExtracted
                            ? {
                                borderLeft: "2px solid #F59E0B",
                                pl: 1,
                                marginTop: 1,
                                borderRadius: "4px",
                                backgroundColor: "#FFFDF5",
                              }
                            : {}
                        }
                      >
                        <Grid
                          container
                          spacing={2}
                          alignItems="center"
                          sx={{
                            py: 1,
                            borderBottom:
                              index !== rows.length - 1
                                ? "1px solid #E1E1E1"
                                : "none",
                          }}
                        >
                          <Grid item xs={12} md={0.2}>
                            <Typography>{index + 1}</Typography>
                          </Grid>

                          {/* Test Name */}
                          <Grid
                            item
                            xs={12}
                            md={
                              !row.test?.__isNew__ && row.isComposite ? 11.3 : 6
                            }
                          >
                            <CreatableSelect
                              components={{
                                DropdownIndicator: (props) => (
                                  <components.DropdownIndicator {...props}>
                                    <Search sx={{ color: "#999" }} />
                                  </components.DropdownIndicator>
                                ),
                                IndicatorSeparator: () => null,
                                LoadingIndicator: () => null,
                              }}
                              isSearchable
                              noOptionsMessage={() => {
                                if (isTyping) return "Loading...";
                                if (medicalTestLoading)
                                  return "Searching tests...";
                                if (debouncedTestSearch)
                                  return "No tests found";
                                return "Start typing to search";
                              }}
                              isValidNewOption={() => canCreate}
                              onCreateOption={(val) =>
                                openCreateTypeModal(index, val)
                              }
                              isMulti={false}
                              isLoading={medicalTestLoading}
                              options={shouldFetchTests ? testOptions : []}
                              value={row.test}
                              placeholder="Select or Create Test"
                              onInputChange={(val) => setTestSearch(val)}
                              onChange={(selected) => {
                                const updated = [...rows];
                                updated[index].test = selected;
                                updated[index].reportId = null; // ✅ reset on test change

                                const existingComposite =
                                  isCompositeFromApi(selected);
                                updated[index].isComposite = existingComposite;

                                if (existingComposite) {
                                  updated[index].value = "";
                                  updated[index].compositeRows = (
                                    selected?.value?.composite_fields || []
                                  ).map((t) => ({
                                    test: { value: t, label: t.name },
                                    value: "",
                                    reportId: null, // ✅
                                  }));
                                } else {
                                  updated[index].compositeRows = [];
                                }

                                setRows(updated);
                              }}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuPlacement="auto"
                              maxMenuHeight={220}
                              menuShouldScrollIntoView={false}
                              styles={selectStyles}
                            />
                          </Grid>

                          {/* Value */}
                          {row.isComposite ? (
                            row.test?.__isNew__ && (
                              <Grid item xs={12} md={5.3}>
                                <Select
                                  components={{
                                    DropdownIndicator: (props) => (
                                      <components.DropdownIndicator {...props}>
                                        <Search sx={{ color: "#999" }} />
                                      </components.DropdownIndicator>
                                    ),
                                    IndicatorSeparator: () => null,
                                  }}
                                  options={formattedLabOptions}
                                  value={row.lab_id}
                                  onChange={(selected) => {
                                    const updated = [...rows];
                                    updated[index].lab_id = selected;
                                    setRows(updated);
                                  }}
                                  placeholder="Choose lab"
                                  menuPortalTarget={document.body}
                                  styles={selectStyles}
                                />
                              </Grid>
                            )
                          ) : (
                            <Grid item xs={12} md={5.3}>
                              <TextField
                                fullWidth
                                placeholder="Value"
                                value={row?.value}
                                disabled={!row.test || rowLoading[index]}
                                sx={{
                                  ...textfieldText,
                                  borderRadius: "10px",
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    height: "45px",
                                    fontFamily: "Lato",
                                  },
                                }}
                                InputProps={{
                                  endAdornment: rowLoading[
                                    `ai_confirm_${index}`
                                  ] ? (
                                    <CircularProgress
                                      size={18}
                                      sx={{ mr: 0.5 }}
                                    />
                                  ) : rowLoading[index] ? (
                                    <CircularProgress
                                      size={18}
                                      sx={{ mr: 0.5 }}
                                    />
                                  ) : row.aiExtracted &&
                                    !row.test?.__isNew__ ? (
                                    <Tooltip title="AI parsed — review value and confirm to save">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleConfirmSingleAiRow(row, index)
                                        }
                                        sx={{ color: "#F59E0B", p: 0.3 }}
                                      >
                                        <CheckCircleOutlineIcon
                                          sx={{ fontSize: 20 }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  ) : null,
                                }}
                                onChange={(e) => {
                                  const updated = [...rows];
                                  updated[index].value = e.target.value;
                                  setRows(updated);
                                }}
                                onFocus={() => {
                                  if (row?.reportId) {
                                    const key = makeKey("normal", row.reportId);
                                    if (
                                      originalValueRef.current[key] ===
                                      undefined
                                    ) {
                                      originalValueRef.current[key] =
                                        row.value ?? "";
                                    }
                                  }
                                }}
                                onBlur={() => {
                                  if (row.test?.__isNew__) return;
                                  handleValueBlur(row, index);
                                }}
                              />
                            </Grid>
                          )}

                          {/* Delete Row */}
                          <Grid
                            item
                            xs={12}
                            md={0.5}
                            display="flex"
                            justifyContent="center"
                          >
                            <IconButton
                              sx={
                                {
                                  // marginBottom: "7px",
                                  // height: "28px",
                                  // display: "flex",
                                  // // marginLeft: "auto",
                                  // alignSelf: "flex-end",
                                }
                              }
                              onClick={() => handleRemoveRow(index)}
                            >
                              <CloseIcon
                                sx={{
                                  ...xBtn,
                                  width: "22px",
                                  height: "22px",
                                  padding: "2px",
                                }}
                              />
                            </IconButton>
                          </Grid>

                          {/* Extra fields for new normal test */}
                          {isNew && !row.isComposite && (
                            <Grid
                              container
                              spacing={2}
                              sx={{ pb: 2, pt: 1, pl: { xs: 0, md: 4 } }}
                            >
                              <Grid item xs={12} md={4.5}>
                                <Select
                                  components={{
                                    DropdownIndicator: (props) => (
                                      <components.DropdownIndicator {...props}>
                                        <Search sx={{ color: "#999" }} />
                                      </components.DropdownIndicator>
                                    ),
                                    IndicatorSeparator: () => null,
                                  }}
                                  options={formattedLabOptions}
                                  value={row.lab_id}
                                  onChange={(selected) => {
                                    const updated = [...rows];
                                    updated[index].lab_id = selected;
                                    setRows(updated);
                                  }}
                                  placeholder="Choose lab"
                                  menuPortalTarget={document.body}
                                  styles={selectStyles}
                                />
                              </Grid>

                              <Grid item xs={6} md={2}>
                                <TextField
                                  fullWidth
                                  placeholder="Min Range"
                                  value={row.min || ""}
                                  sx={{
                                    ...textfieldText,
                                    borderRadius: "10px",
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "10px",
                                      fontFamily: "Lato",
                                      height: "45px",
                                    },
                                  }}
                                  onChange={(e) => {
                                    const updated = [...rows];
                                    updated[index].min = e.target.value;
                                    setRows(updated);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={6} md={2}>
                                <TextField
                                  fullWidth
                                  placeholder="Max Range"
                                  value={row.max || ""}
                                  sx={{
                                    ...textfieldText,
                                    borderRadius: "10px",
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "10px",
                                      height: "45px",
                                      fontFamily: "Lato",
                                    },
                                  }}
                                  onChange={(e) => {
                                    const updated = [...rows];
                                    updated[index].max = e.target.value;
                                    setRows(updated);
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  placeholder="Critical Range"
                                  value={row.critical || ""}
                                  sx={{
                                    ...textfieldText,
                                    borderRadius: "10px",
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "10px",
                                      height: "45px",
                                      fontFamily: "Lato",
                                    },
                                  }}
                                  onChange={(e) => {
                                    const updated = [...rows];
                                    updated[index].critical = e.target.value;
                                    setRows(updated);
                                  }}
                                />
                              </Grid>

                              {!row?.isComposite && (
                                <Grid item xs={12} md={3}>
                                  <Button
                                    sx={{
                                      ...primaryBtnTeal,
                                      height: "45px",
                                      borderRadius: "10px",
                                    }}
                                    fullWidth
                                    disabled={
                                      isNewTestIncomplete(row) ||
                                      !row.value ||
                                      rowLoading[`create_${index}`]
                                    }
                                    onClick={() =>
                                      handleCreateNormalTest(row, index)
                                    }
                                  >
                                    {rowLoading[`create_${index}`]
                                      ? "Creating..."
                                      : "Create test"}
                                  </Button>
                                </Grid>
                              )}
                            </Grid>
                          )}
                        </Grid>

                        {/* Composite Sub Table */}
                        {row.isComposite && (
                          <Box
                            sx={{
                              mt: 1.5,
                              mb: 2,
                              borderRadius: "14px",
                              border: "1.5px solid #C5E8E4",
                              overflow: "hidden",
                              background: "#fff",
                              boxShadow: "0 2px 8px rgba(43,132,126,0.06)",
                            }}
                          >
                            {/* Header */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                px: 2,
                                py: 1.25,
                                background:
                                  "linear-gradient(90deg, #EEF9F7 0%, #F4FDFC 100%)",
                                borderBottom: "1.5px solid #C5E8E4",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    background: "#2B847E",
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontFamily: "Lato",
                                    fontWeight: 700,
                                    fontSize: "13px",
                                    color: "#2B847E",
                                    letterSpacing: "0.3px",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Sub Tests
                                </Typography>
                                {row.compositeRows?.length > 0 && (
                                  <Box
                                    sx={{
                                      background: "#2B847E",
                                      color: "#fff",
                                      borderRadius: "20px",
                                      px: 1,
                                      py: 0.1,
                                      fontSize: "11px",
                                      fontFamily: "Lato",
                                      fontWeight: 700,
                                      lineHeight: "18px",
                                    }}
                                  >
                                    {row.compositeRows.length}
                                  </Box>
                                )}
                              </Box>

                              <Box sx={{ display: "flex", gap: 2 }}>
                                {!locked && (
                                  <Button
                                    onClick={() => addCompositeSubRow(index)}
                                    startIcon={<AddIcon />}
                                    sx={{
                                      ...primaryBtnTeal,
                                      height: "34px",
                                      borderRadius: "10px",
                                      px: 2,
                                    }}
                                    disabled={!row.test}
                                  >
                                    Add Sub Test
                                  </Button>
                                )}

                                {row.test?.__isNew__ && (
                                  <Button
                                    onClick={() =>
                                      handleCreateCompositeAndReports(index)
                                    }
                                    sx={{
                                      ...primaryBtnTeal,
                                      height: "34px",
                                      borderRadius: "10px",
                                      px: 2,
                                    }}
                                    disabled={
                                      rowLoading[`comp_create_${index}`]
                                    }
                                  >
                                    {rowLoading[`comp_create_${index}`]
                                      ? "Saving..."
                                      : "Save Composite"}
                                  </Button>
                                )}

                                {row.aiExtracted && !row.test?.__isNew__ && (
                                  <Tooltip title="AI parsed — review sub-test values and confirm to save">
                                    <Button
                                      onClick={() =>
                                        handleConfirmAiCompositeRow(index)
                                      }
                                      startIcon={
                                        rowLoading[
                                          `ai_comp_confirm_${index}`
                                        ] ? (
                                          <CircularProgress
                                            size={14}
                                            sx={{ color: "white" }}
                                          />
                                        ) : (
                                          <CheckCircleOutlineIcon />
                                        )
                                      }
                                      sx={{
                                        ...primaryBtnTeal,
                                        backgroundColor: "#F59E0B",
                                        "&:hover": {
                                          backgroundColor: "#D97706",
                                        },
                                        height: "34px",
                                        borderRadius: "10px",
                                        px: 2,
                                      }}
                                      disabled={
                                        rowLoading[`ai_comp_confirm_${index}`]
                                      }
                                    >
                                      {rowLoading[`ai_comp_confirm_${index}`]
                                        ? "Saving..."
                                        : "Confirm"}
                                    </Button>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>

                            <TableContainer sx={{ px: 1, pt: 0, pb: 1 }}>
                              <Table
                                size="small"
                                sx={{
                                  tableLayout: "fixed",
                                  "& .MuiTableCell-root": {
                                    borderBottom: "none",
                                    verticalAlign: "middle",
                                    py: 0.6,
                                    px: 0.75,
                                  },
                                  "& .MuiTableHead-root .MuiTableCell-root": {
                                    borderBottom: "1px solid #D1EDEA",
                                    py: 0.75,
                                    color: "#38A3B9",
                                    fontSize: "11px",
                                    fontFamily: "Lato",
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    background: "#F0FEFB",
                                  },
                                }}
                              >
                                <TableHead>
                                  <TableRow>
                                    <TableCell
                                      sx={{
                                        width: "32px",
                                        textAlign: "center",
                                      }}
                                    >
                                      #
                                    </TableCell>
                                    <TableCell sx={{ width: "54%" }}>
                                      Test Name
                                    </TableCell>
                                    <TableCell sx={{ width: "34%" }}>
                                      Value
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        width: "36px",
                                        textAlign: "center",
                                      }}
                                    />
                                  </TableRow>
                                </TableHead>

                                <TableBody>
                                  {(row.compositeRows || []).length === 0 ? (
                                    <TableRow>
                                      <TableCell
                                        colSpan={4}
                                        sx={{ py: 3, textAlign: "center" }}
                                      >
                                        <Typography
                                          sx={{
                                            color: "#9CA3AF",
                                            fontFamily: "Lato",
                                            fontSize: "13px",
                                          }}
                                        >
                                          No sub-tests added yet
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    (row.compositeRows || []).map(
                                      (subRow, subIndex) => {
                                        const key = `c_${index}_${subIndex}`;
                                        const isNewSub = Boolean(
                                          subRow?.test?.__isNew__,
                                        );
                                        const isEvenRow = subIndex % 2 === 0;

                                        return (
                                          <React.Fragment key={key}>
                                            <TableRow
                                              sx={{
                                                background: isEvenRow
                                                  ? "#fff"
                                                  : "#F7FEFD",
                                                "&:hover": {
                                                  background: "#EEF9F7",
                                                },
                                                transition: "background 0.15s",
                                              }}
                                            >
                                              <TableCell
                                                sx={{
                                                  textAlign: "center",
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: "50%",
                                                    background: "#E8F7F5",
                                                    color: "#2B847E",
                                                    fontSize: "11px",
                                                    fontFamily: "Lato",
                                                    fontWeight: 700,
                                                  }}
                                                >
                                                  {subIndex + 1}
                                                </Box>
                                              </TableCell>

                                              <TableCell>
                                                {locked ? (
                                                  <Box
                                                    sx={{
                                                      height: "36px",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      px: 1.5,
                                                      borderRadius: "8px",
                                                      background: "#F0FEFB",
                                                      border:
                                                        "1px solid #C5E8E4",
                                                    }}
                                                  >
                                                    <Typography
                                                      sx={{
                                                        fontFamily: "Lato",
                                                        fontSize: "13.5px",
                                                        color: "#2B847E",
                                                        fontWeight: 500,
                                                      }}
                                                    >
                                                      {subRow?.test?.label ||
                                                        subRow?.test?.value
                                                          ?.name}
                                                    </Typography>
                                                  </Box>
                                                ) : (
                                                  <CreatableSelect
                                                    components={{
                                                      DropdownIndicator: (
                                                        props,
                                                      ) => (
                                                        <components.DropdownIndicator
                                                          {...props}
                                                        >
                                                          <Search
                                                            sx={{
                                                              color: "#999",
                                                            }}
                                                          />
                                                        </components.DropdownIndicator>
                                                      ),
                                                      IndicatorSeparator: () =>
                                                        null,
                                                      LoadingIndicator: () =>
                                                        null,
                                                    }}
                                                    isSearchable
                                                    isMulti={false}
                                                    isValidNewOption={() =>
                                                      canCreate
                                                    }
                                                    isLoading={
                                                      medicalTestLoading
                                                    }
                                                    options={
                                                      shouldFetchTests
                                                        ? subTestOptions
                                                        : []
                                                    }
                                                    value={subRow.test}
                                                    placeholder="Select or Create Sub Test"
                                                    onInputChange={(val) =>
                                                      setTestSearch(val)
                                                    }
                                                    onChange={(selected) => {
                                                      const updated = [...rows];
                                                      updated[
                                                        index
                                                      ].compositeRows[
                                                        subIndex
                                                      ].test = selected;
                                                      updated[
                                                        index
                                                      ].compositeRows[
                                                        subIndex
                                                      ].reportId = null;

                                                      if (
                                                        selected?.__isNew__ ||
                                                        selected?.value ===
                                                          selected?.label
                                                      ) {
                                                        updated[
                                                          index
                                                        ].compositeRows[
                                                          subIndex
                                                        ].lab_id =
                                                          updated[index]
                                                            ?.lab_id ?? null;
                                                        updated[
                                                          index
                                                        ].compositeRows[
                                                          subIndex
                                                        ].min = "";
                                                        updated[
                                                          index
                                                        ].compositeRows[
                                                          subIndex
                                                        ].max = "";
                                                        updated[
                                                          index
                                                        ].compositeRows[
                                                          subIndex
                                                        ].critical = "";
                                                      }

                                                      setRows(updated);
                                                    }}
                                                    menuPortalTarget={
                                                      document.body
                                                    }
                                                    styles={{
                                                      ...selectStyles,
                                                      control: (
                                                        base,
                                                        state,
                                                      ) => ({
                                                        ...selectStyles.control(
                                                          base,
                                                          state,
                                                        ),
                                                        minHeight: "38px",
                                                      }),
                                                    }}
                                                  />
                                                )}
                                              </TableCell>

                                              <TableCell>
                                                <TextField
                                                  fullWidth
                                                  placeholder="Enter value"
                                                  value={subRow.value || ""}
                                                  disabled={
                                                    !subRow.test ||
                                                    rowLoading[key]
                                                  }
                                                  sx={{
                                                    "& .MuiOutlinedInput-root":
                                                      {
                                                        borderRadius: "8px",
                                                        height: "36px",
                                                        fontFamily: "Lato",
                                                        fontSize: "13.5px",
                                                        "& fieldset": {
                                                          borderColor:
                                                            "#C5E8E4",
                                                        },
                                                        "&:hover fieldset": {
                                                          borderColor:
                                                            "#38A3B9",
                                                        },
                                                        "&.Mui-focused fieldset":
                                                          {
                                                            borderColor:
                                                              "#2B847E",
                                                          },
                                                      },
                                                  }}
                                                  InputProps={{
                                                    endAdornment: rowLoading[
                                                      key
                                                    ] ? (
                                                      <CircularProgress
                                                        size={14}
                                                        sx={{
                                                          mr: 0.5,
                                                          color: "#2B847E",
                                                        }}
                                                      />
                                                    ) : null,
                                                  }}
                                                  onChange={(e) => {
                                                    const updated = [...rows];
                                                    updated[
                                                      index
                                                    ].compositeRows[
                                                      subIndex
                                                    ].value = e.target.value;
                                                    setRows(updated);
                                                  }}
                                                  onBlur={() => {
                                                    if (
                                                      rows[index]?.test
                                                        ?.__isNew__
                                                    )
                                                      return;
                                                    handleCompositeValueBlur(
                                                      index,
                                                      subIndex,
                                                    );
                                                  }}
                                                  onFocus={() => {
                                                    if (subRow?.reportId) {
                                                      const key = makeKey(
                                                        "comp",
                                                        subRow.reportId,
                                                      );
                                                      if (
                                                        originalValueRef
                                                          .current[key] ===
                                                        undefined
                                                      ) {
                                                        originalValueRef.current[
                                                          key
                                                        ] = subRow.value ?? "";
                                                      }
                                                    }
                                                  }}
                                                />
                                              </TableCell>

                                              <TableCell
                                                sx={{ textAlign: "center" }}
                                              >
                                                {!locked && (
                                                  <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                      removeCompositeSubRow(
                                                        index,
                                                        subIndex,
                                                      )
                                                    }
                                                    sx={{
                                                      p: 0.4,
                                                      color: "#EF4444",
                                                      "&:hover": {
                                                        background: "#FEE2E2",
                                                      },
                                                      borderRadius: "6px",
                                                    }}
                                                  >
                                                    <CloseIcon
                                                      sx={{
                                                        width: 16,
                                                        height: 16,
                                                      }}
                                                    />
                                                  </IconButton>
                                                )}
                                              </TableCell>
                                            </TableRow>

                                            {/* Extra fields for new sub-test */}
                                            {!locked && isNewSub && (
                                              <TableRow>
                                                <TableCell
                                                  colSpan={4}
                                                  sx={{ pb: 1.5, pt: 0 }}
                                                >
                                                  <Box
                                                    sx={{
                                                      background: "#F5F8FF",
                                                      border:
                                                        "1px dashed #C9D5F0",
                                                      borderRadius: "10px",
                                                      p: 1.5,
                                                    }}
                                                  >
                                                    <Grid
                                                      container
                                                      spacing={1.5}
                                                    >
                                                      <Grid
                                                        item
                                                        xs={12}
                                                        md={4.5}
                                                      >
                                                        <Select
                                                          options={
                                                            formattedLabOptions
                                                          }
                                                          value={subRow.lab_id}
                                                          onChange={(
                                                            selected,
                                                          ) => {
                                                            const updated = [
                                                              ...rows,
                                                            ];
                                                            updated[
                                                              index
                                                            ].compositeRows[
                                                              subIndex
                                                            ].lab_id = selected;
                                                            setRows(updated);
                                                          }}
                                                          placeholder="Choose lab"
                                                          menuPortalTarget={
                                                            document.body
                                                          }
                                                          styles={{
                                                            ...selectStyles,
                                                            control: (
                                                              base,
                                                              state,
                                                            ) => ({
                                                              ...selectStyles.control(
                                                                base,
                                                                state,
                                                              ),
                                                              minHeight: "38px",
                                                            }),
                                                          }}
                                                        />
                                                      </Grid>

                                                      <Grid item xs={4} md={2}>
                                                        <TextField
                                                          fullWidth
                                                          placeholder="Min"
                                                          value={
                                                            subRow.min || ""
                                                          }
                                                          size="small"
                                                          sx={{
                                                            ...textfieldText,
                                                            "& .MuiOutlinedInput-root":
                                                              {
                                                                borderRadius:
                                                                  "8px",
                                                                height: "38px",
                                                                fontFamily:
                                                                  "Lato",
                                                              },
                                                          }}
                                                          onChange={(e) => {
                                                            const updated = [
                                                              ...rows,
                                                            ];
                                                            updated[
                                                              index
                                                            ].compositeRows[
                                                              subIndex
                                                            ].min =
                                                              e.target.value;
                                                            setRows(updated);
                                                          }}
                                                        />
                                                      </Grid>

                                                      <Grid item xs={4} md={2}>
                                                        <TextField
                                                          fullWidth
                                                          placeholder="Max"
                                                          value={
                                                            subRow.max || ""
                                                          }
                                                          size="small"
                                                          sx={{
                                                            ...textfieldText,
                                                            "& .MuiOutlinedInput-root":
                                                              {
                                                                borderRadius:
                                                                  "8px",
                                                                height: "38px",
                                                                fontFamily:
                                                                  "Lato",
                                                              },
                                                          }}
                                                          onChange={(e) => {
                                                            const updated = [
                                                              ...rows,
                                                            ];
                                                            updated[
                                                              index
                                                            ].compositeRows[
                                                              subIndex
                                                            ].max =
                                                              e.target.value;
                                                            setRows(updated);
                                                          }}
                                                        />
                                                      </Grid>

                                                      <Grid item xs={4} md={3}>
                                                        <TextField
                                                          fullWidth
                                                          placeholder="Critical"
                                                          value={
                                                            subRow.critical ||
                                                            ""
                                                          }
                                                          size="small"
                                                          sx={{
                                                            ...textfieldText,
                                                            "& .MuiOutlinedInput-root":
                                                              {
                                                                borderRadius:
                                                                  "8px",
                                                                height: "38px",
                                                                fontFamily:
                                                                  "Lato",
                                                              },
                                                          }}
                                                          onChange={(e) => {
                                                            const updated = [
                                                              ...rows,
                                                            ];
                                                            updated[
                                                              index
                                                            ].compositeRows[
                                                              subIndex
                                                            ].critical =
                                                              e.target.value;
                                                            setRows(updated);
                                                          }}
                                                        />
                                                      </Grid>
                                                    </Grid>
                                                  </Box>
                                                </TableCell>
                                              </TableRow>
                                            )}
                                          </React.Fragment>
                                        );
                                      },
                                    )
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          )}
        </Box>
      </>

      {/* Create Type Modal */}
      <Dialog
        open={createModal.open}
        onClose={closeCreateTypeModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "Lato", fontWeight: 700 }}>
          Create Test
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: "Lato", mb: 1, color: "#667085" }}>
            Create <b>{createModal.label}</b> as:
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={createChoice === "normal"}
                  onChange={() => setCreateChoice("normal")}
                  sx={{ "&.Mui-checked": { color: "neutral.teal" } }}
                />
              }
              label={<Typography sx={{ fontFamily: "Lato" }}>Test</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={createChoice === "composite"}
                  onChange={() => setCreateChoice("composite")}
                  sx={{ "&.Mui-checked": { color: "neutral.teal" } }}
                />
              }
              label={
                <Typography sx={{ fontFamily: "Lato" }}>
                  Composite Test
                </Typography>
              }
            />
          </Box>

          <Typography
            sx={{ fontFamily: "Lato", mt: 1, fontSize: 12, color: "#667085" }}
          >
            * Composite test will show a sub-table for adding sub-tests.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeCreateTypeModal}
            sx={{ borderRadius: "10px", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmCreateType}
            sx={{
              ...primaryBtnTeal,
              height: "36px",
              borderRadius: "10px",
              px: 2,
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Modal
        open={viewerOpen}
        onClose={closeViewer}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            width: "90vw",
            height: "90vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: 24,
            position: "relative",
          }}
        >
          <Box
            sx={{
              height: 54,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>
              {viewerFile?.filename || "Preview"}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Button
                size="small"
                onClick={() =>
                  viewerFile?.url && window.open(viewerFile.url, "_blank")
                }
              >
                <ScreenShareOutlinedIcon sx={{ color: "neutral.teal" }} />
              </Button>
              <IconButton onClick={closeViewer}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ width: "100%", height: "calc(90vh - 54px)" }}>
            {viewerFile?.content_type === "application/pdf" ? (
              <iframe
                title="pdf-viewer"
                src={viewerFile?.url}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            ) : viewerFile?.content_type?.startsWith("image/") ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#000",
                }}
              >
                <img
                  src={viewerFile?.url}
                  alt={viewerFile?.filename || "image"}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography>
                  Preview not supported for this file type.
                </Typography>
                <Button
                  sx={{ mt: 2 }}
                  variant="contained"
                  onClick={() =>
                    viewerFile?.url && window.open(viewerFile.url, "_blank")
                  }
                >
                  Download / Open
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default AddExternalLabReports;
